import {
  CAR_PRICE,
  GARAGE_UPGRADE_PRICE,
  REGISTRATION_PRICE,
  plateStates,
  stateSlogan,
} from '@hotlist/shared';

import type { CarKey } from './keyring.ts';

/**
 * The Garage: the cars and the money. Nothing else.
 *
 * Identity is not in here at all any more. Saving the game happens in the
 * click that starts it -- the landing card's Drive asks the device for its
 * own unlock right then -- so there is no save button to go hunting for, no
 * sign-in for somebody already playing, and no state line. A panel that had
 * grown six identity controls across four design eras now has zero, which is
 * the correct number for a garage.
 */
export interface GarageHandlers {
  switchTo: (plate: string) => void;
  buy: (what: 'registration' | 'vehicle' | 'garage') => void;
  /** End the session: back out to the front door, keys forgotten here. */
  signOut: () => void;
  /** Ask whether a typed custom plate is free and legal. */
  plateCheck: (plate: string, state: string) => void;
  /** Buy the custom plate that just checked out clean. */
  plateBuy: (plate: string, state: string) => void;
}

export class GaragePanel {
  private readonly list: HTMLElement;
  private readonly note: HTMLElement;
  private readonly buys: HTMLElement;

  constructor(handlers: GarageHandlers) {
    this.list = must('garage-list');
    this.note = must('garage-note');
    this.buys = must('garage-buys');
    this.list.addEventListener('click', (event) => {
      const plate = (event.target as HTMLElement).dataset.plate;
      if (plate) handlers.switchTo(plate);
    });

    this.buys.addEventListener('click', (event) => {
      const what = (event.target as HTMLElement).dataset.buy;
      if (what === 'registration' || what === 'vehicle' || what === 'garage') handlers.buy(what);
    });

    must('sign-out').addEventListener('click', () => handlers.signOut());

    /**
     * The custom-plate store has three acts: preview, check, then buy.
     *
     * The preview plate paints live as the serial and state are chosen, so the
     * thing the dollar buys is on screen before the dollar is spent. A check
     * asks the server and the answer is a plain word -- available or taken --
     * never left to be read off whether a button appeared. The Buy button only
     * exists after a clean check, and any edit afterward clears that answer, so
     * nobody pays off a verdict that was about different text.
     */
    const plateForm = must('garage-plate') as HTMLFormElement;
    const plateInput = must('plate-want') as HTMLInputElement;
    const statePick = must('plate-state') as HTMLSelectElement;
    const preview = must('store-preview');
    const previewSerial = must('store-plate-serial');
    const previewState = must('store-plate-state');
    const previewStrap = must('store-plate-strap');

    const stateName = new Map<string, string>();
    for (const state of plateStates()) {
      stateName.set(state.abbr, state.name);
      const option = document.createElement('option');
      option.value = state.abbr;
      option.textContent = `${state.abbr} — ${state.name}`;
      statePick.append(option);
    }

    const paintPreview = (): void => {
      const serial = plateInput.value.trim().toUpperCase();
      previewSerial.textContent = serial || 'YOUR TAG';
      previewState.textContent = (stateName.get(statePick.value) ?? statePick.value).toUpperCase();
      previewStrap.textContent = stateSlogan(statePick.value);
    };
    const clearVerdict = (): void => {
      must('plate-say').replaceChildren();
      preview.classList.remove('available', 'taken');
    };

    plateInput.addEventListener('input', () => {
      paintPreview();
      clearVerdict();
    });
    statePick.addEventListener('change', () => {
      paintPreview();
      clearVerdict();
    });
    paintPreview();

    plateForm.addEventListener('submit', (event) => {
      event.preventDefault();
      const plate = plateInput.value.trim().toUpperCase();
      if (plate) handlers.plateCheck(plate, statePick.value);
    });
    must('plate-say').addEventListener('click', (event) => {
      const plate = (event.target as HTMLElement).dataset.buy;
      if (plate) handlers.plateBuy(plate, statePick.value);
    });
  }

  /**
   * What a tag costs, what a car costs, and whether this player can afford
   * either.
   *
   * The balance comes along because a price with no balance beside it cannot
   * be read, but it is a note here rather than the subject.
   */
  setPurse(money: number, wanted: boolean, capacity = 1, owned = 1): void {
    this.buys.replaceChildren();

    const balance = document.createElement('div');
    balance.className = 'garage-money';
    balance.textContent = `$${money}`;
    this.buys.append(balance);

    if (wanted) {
      const flag = document.createElement('div');
      flag.className = 'work-wanted';
      flag.textContent = 'your plate is on a list';
      this.buys.append(flag);
    }

    // What the garage holds, and how full it is. A car cannot be bought past it.
    const full = owned >= capacity;
    const status = document.createElement('div');
    status.className = 'garage-capacity';
    status.textContent = `Garage holds ${capacity} car${capacity === 1 ? '' : 's'} — ${owned} parked${full ? ', full' : ''}.`;
    this.buys.append(status);

    const row = document.createElement('div');
    row.className = 'work-buys';
    const items: readonly [string, string, number, boolean][] = [
      ['registration', 'renew tag', REGISTRATION_PRICE, money < REGISTRATION_PRICE],
      ['vehicle', 'buy a car', CAR_PRICE, money < CAR_PRICE || full],
      ['garage', 'bigger garage', GARAGE_UPGRADE_PRICE, money < GARAGE_UPGRADE_PRICE],
    ];
    for (const [what, label, price, disabled] of items) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'work-buy';
      button.dataset.buy = what;
      button.textContent = `${label} $${price}`;
      button.disabled = disabled;
      row.append(button);
    }
    this.buys.append(row);
  }

  /**
   * The verdict on a checked plate, said in a word.
   *
   * Available paints the preview green and lays down an explicit line plus the
   * Buy button; taken or illegal paints it red and says why. The point the
   * player complained about: the answer is stated, not implied by a button.
   */
  plateVerdict(plate: string, ok: boolean, reason: string, priceCents: number): void {
    const say = must('plate-say');
    const preview = must('store-preview');
    say.replaceChildren();
    preview.classList.toggle('available', ok);
    preview.classList.toggle('taken', !ok);

    const verdict = document.createElement('div');
    if (ok) {
      verdict.className = 'store-verdict ok';
      verdict.textContent = `${plate} is available.`;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'store-buy';
      button.dataset.buy = plate;
      button.textContent = `Pay $${(priceCents / 100).toFixed(2)} USD`;
      say.append(verdict, button);
    } else {
      verdict.className = 'store-verdict no';
      verdict.textContent = reason ? `${plate} — ${reason}.` : `${plate} is not available.`;
      say.append(verdict);
    }
  }

  show(cars: readonly CarKey[], driving: string): void {
    this.list.replaceChildren();

    for (const car of cars) {
      const row = document.createElement('div');
      row.className = car.plate === driving ? 'garage-row driving' : 'garage-row';

      const plate = document.createElement('span');
      plate.className = 'garage-plate';
      plate.textContent = car.plate;

      const action = document.createElement('button');
      action.type = 'button';
      action.className = 'garage-switch';
      if (car.plate === driving) {
        action.textContent = 'driving';
        action.disabled = true;
      } else {
        action.textContent = 'drive';
        action.dataset.plate = car.plate;
      }

      row.append(plate, action);
      this.list.append(row);
    }

    this.note.textContent = '';
  }
}

function must(id: string): HTMLElement {
  const element = document.getElementById(id);
  if (!element) throw new Error(`missing element #${id}`);
  return element;
}
